import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'
import { generateUrls as artworkUrls } from '@hooks/useArtwork'
import { generateUrls as campaignQuoteUrls } from '@hooks/useCampaignQuote'

import * as conversationActions from '@redux/modules/conversation'

import defaultFormState, { requiredFields } from '@models/conversation'

import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'
import type { ConversationModel, ConversationRequestOptions } from '@models/types'
import { EntitiesState } from '@redux/entities'
import useRedirectUrlWithCacheKey from './useRedirectUrlWithCacheKey'

type GenerateUrlsOptions = {
  conversationId: number,
  prefixRoute?: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { conversationId, prefixRoute = 'systemManager' } = options || {}

  return {
    conversationsIndexUrl: `#/${prefixRoute}/conversations/${conversationId}`,
  }
}

type GetSubjectUrlParams = {
  conversation: ConversationModel,
  artworks: EntitiesState['artworks'],
  redirectUrl: string,
}

const getSubjectUrl = (params: GetSubjectUrlParams) => {
  const { conversation, artworks, redirectUrl } = params

  const {
    campaign_id, subject_id, subject_type,
  } = conversation

  if (!subject_id) return null

  if (subject_type === 'Artwork'){
    const artwork = artworks[subject_id] || {}

    const { editArtworkUrl } = artworkUrls(artwork, { includeRedirect: true, redirectUrl })

    if (artwork.id) return editArtworkUrl
  }

  if (subject_type === 'CampaignQuote'){
    const { editCampaignQuoteUrl } = campaignQuoteUrls(
      { id: subject_id },
      { prefixRoute: `campaigns/${campaign_id}/edit` },
    )

    return editCampaignQuoteUrl
  }

  if (subject_type === 'Image'){
    return `#/campaigns/${campaign_id}/edit/media`
  }

  return null
}

type CreateSubjectConversationParams = {
  conversationParams: Partial<ConversationModel>,
  dispatch: AppDispatch,
  requestOptions?: ConversationRequestOptions,
}

const createSubjectConversation = (params: CreateSubjectConversationParams) => {
  const { conversationParams, dispatch, requestOptions } = params
  const { createSubjectConversation: createFn } = conversationActions

  return dispatch(createFn(conversationParams, requestOptions))
}

type AutoSelectConversationParams = {
  campaignId: number,
  conversationId: number,
  filteredConversations: ConversationModel[],
  selectedConversation: ConversationModel,
}

const autoSelectConversation = (params: AutoSelectConversationParams) => {
  const {
    selectedConversation, conversationId, filteredConversations, campaignId,
  } = params

  if (!selectedConversation?.id && !conversationId && filteredConversations.length){
    const firstConversation = filteredConversations[0] || {}

    const prefix = `campaigns/${campaignId}/edit`
    window.location.hash = `#/${prefix}/conversations/${firstConversation.id}`
  }
}

type MarkCommentsAsReadParams = {
  conversation: Partial<ConversationModel>,
  dispatch: AppDispatch,
  requestOptions?: ConversationRequestOptions,
}

const markCommentsAsRead = (params: MarkCommentsAsReadParams) => {
  const { conversation, requestOptions, dispatch } = params

  const { markCommentsAsRead: markCommentsAsReadFn } = conversationActions

  return dispatch(markCommentsAsReadFn(conversation, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useConversationForm(
  conversation: Partial<ConversationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const conversationForm = useForm(
    defaultFormState,
    { entity: conversation, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [conversation.id, conversation.cache_key],
  )

  return {
    ...conversationForm,
  }
}

export const useRelations = (conversation: Partial<ConversationModel> = {}) => {
  // @ts-expect-error TS2339
  const { last_comment } = conversation

  const { comments } = useSelector(reduxState => reduxState.entities)

  const lastComment = last_comment && comments[last_comment] ? comments[last_comment] : {}

  return {
    lastComment,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ConversationRequestOptions,
}

function useConversation(initEntity: Partial<ConversationModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: conversation }: { entity: ConversationModel} = useLatestEntity(initEntity, 'conversations')

  const { id } = conversation

  const dispatch = useDispatch()

  const { lastComment } = useRelations(conversation)

  const { redirectUrl } = useRedirectUrlWithCacheKey([conversation.id])

  useReduxAction(
    'conversations',
    'loadConversation',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, loadedIds, loading,
  } = useSelector(reduxState => reduxState.conversations)

  const artworks = useSelector(reduxState => reduxState.entities.artworks)

  return {
    callbacks: {
      autoSelectConversation: (
        campaignId: number,
        conversationId: number,
        filteredConversations: [ConversationModel],
        selectedConversation: ConversationModel,
      ) => (
        autoSelectConversation({
          campaignId, conversationId, filteredConversations, selectedConversation,
        })
      ),
      createSubjectConversation: (
        conversationParams: Partial<ConversationModel>,
        entityOptions?: ConversationRequestOptions,
      ) => (
        createSubjectConversation({ conversationParams, dispatch, requestOptions: entityOptions })
      ),
      markCommentsAsRead: (entityOptions?: ConversationRequestOptions) => markCommentsAsRead({
        conversation, dispatch, requestOptions: entityOptions,
      }),
    },
    conversation,
    creating,
    lastComment,
    loading: loading && !loadedIds.includes(conversation.id),
    subjectUrl: getSubjectUrl({ conversation, artworks, redirectUrl }),
    // @ts-expect-error TS2345
    urls: generateUrls(conversation.id),
  }
}

export type UseConversationPayload = ReturnType<typeof useConversation>

export default useConversation
